import type { Timesheet, Employee } from '@/types/timesheet';
import type { ApprovalPattern, PatternMatch, PatternCriteria, AutomationInsight } from '@/types/approval-automation';

export class ApprovalPatternService {
  private static readonly MIN_PATTERN_MATCHES = 10;
  private static readonly HIGH_CONFIDENCE_THRESHOLD = 85;
  private static readonly MEDIUM_CONFIDENCE_THRESHOLD = 70;
  private static readonly PATTERN_LOOKBACK_DAYS = 90;
  private static readonly AVG_REVIEW_TIME_MINUTES = 3;

  /**
   * Detect approval patterns from historical data
   */
  static detectPatterns(
    approvedTimesheets: Timesheet[],
    employees: Employee[],
    approverId: number
  ): ApprovalPattern[] {
    const patterns: ApprovalPattern[] = [];
    
    // Filter to only timesheets approved by current user in lookback period
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - this.PATTERN_LOOKBACK_DAYS);
    
    const relevantTimesheets = approvedTimesheets.filter(ts => 
      ts.approverId === approverId &&
      ts.approvedAt &&
      ts.approvedAt >= cutoffDate
    );

    if (relevantTimesheets.length < this.MIN_PATTERN_MATCHES) {
      return patterns;
    }

    // Pattern 1: Department-based patterns
    const deptPatterns = this.detectDepartmentPatterns(relevantTimesheets, employees);
    patterns.push(...deptPatterns);

    // Pattern 2: Employee-based patterns (trusted employees)
    const employeePatterns = this.detectEmployeePatterns(relevantTimesheets, employees);
    patterns.push(...employeePatterns);

    // Pattern 3: Hour range patterns
    const hourPatterns = this.detectHourRangePatterns(relevantTimesheets);
    patterns.push(...hourPatterns);

    // Pattern 4: Billable-only patterns
    const billablePatterns = this.detectBillablePatterns(relevantTimesheets);
    patterns.push(...billablePatterns);

    return patterns.filter(p => p.matchCount >= this.MIN_PATTERN_MATCHES);
  }

  private static detectDepartmentPatterns(
    timesheets: Timesheet[],
    employees: Employee[]
  ): ApprovalPattern[] {
    const patterns: ApprovalPattern[] = [];
    const deptGroups = new Map<string, Timesheet[]>();

    // Group by department
    timesheets.forEach(ts => {
      const employee = employees.find(e => e.id === ts.employeeId);
      if (!employee) return;

      const dept = employee.department;
      if (!deptGroups.has(dept)) {
        deptGroups.set(dept, []);
      }
      deptGroups.get(dept)!.push(ts);
    });

    // Create patterns for departments with consistent approvals
    deptGroups.forEach((deptTimesheets, dept) => {
      if (deptTimesheets.length < this.MIN_PATTERN_MATCHES) return;

      // Check if most timesheets have no anomalies and are under reasonable hours
      const cleanTimesheets = deptTimesheets.filter(ts => {
        const totalHours = this.calculateTotalHours(ts);
        return totalHours <= 45; // Reasonable weekly hours
      });

      const confidenceScore = (cleanTimesheets.length / deptTimesheets.length) * 100;

      if (confidenceScore >= this.MEDIUM_CONFIDENCE_THRESHOLD) {
        patterns.push({
          id: `dept-${dept.toLowerCase().replace(/\s+/g, '-')}`,
          name: `${dept} Department Approvals`,
          description: `Automatically approve timesheets from ${dept} department with standard hours`,
          confidence: this.getConfidenceLevel(confidenceScore),
          confidenceScore,
          matchCount: cleanTimesheets.length,
          criteria: {
            departments: [dept],
            maxHoursPerWeek: 45,
            excludeAnomalies: true,
          },
          status: 'suggested',
          createdAt: new Date(),
          lastMatchedAt: deptTimesheets[deptTimesheets.length - 1].approvedAt,
          potentialTimeSavings: cleanTimesheets.length * this.AVG_REVIEW_TIME_MINUTES,
        });
      }
    });

    return patterns;
  }

  private static detectEmployeePatterns(
    timesheets: Timesheet[],
    employees: Employee[]
  ): ApprovalPattern[] {
    const patterns: ApprovalPattern[] = [];
    const employeeGroups = new Map<number, Timesheet[]>();

    // Group by employee
    timesheets.forEach(ts => {
      if (!employeeGroups.has(ts.employeeId)) {
        employeeGroups.set(ts.employeeId, []);
      }
      employeeGroups.get(ts.employeeId)!.push(ts);
    });

    // Create patterns for employees with consistent clean approvals
    employeeGroups.forEach((empTimesheets, empId) => {
      if (empTimesheets.length < this.MIN_PATTERN_MATCHES) return;

      const employee = employees.find(e => e.id === empId);
      if (!employee) return;

      // Check consistency
      const cleanTimesheets = empTimesheets.filter(ts => {
        const totalHours = this.calculateTotalHours(ts);
        return totalHours >= 35 && totalHours <= 45; // Standard work week
      });

      const confidenceScore = (cleanTimesheets.length / empTimesheets.length) * 100;

      if (confidenceScore >= this.HIGH_CONFIDENCE_THRESHOLD) {
        patterns.push({
          id: `emp-${empId}`,
          name: `${employee.name} - Trusted Employee`,
          description: `Automatically approve standard timesheets from ${employee.name}`,
          confidence: this.getConfidenceLevel(confidenceScore),
          confidenceScore,
          matchCount: cleanTimesheets.length,
          criteria: {
            employees: [empId],
            minHoursPerWeek: 35,
            maxHoursPerWeek: 45,
            excludeAnomalies: true,
          },
          status: 'suggested',
          createdAt: new Date(),
          lastMatchedAt: empTimesheets[empTimesheets.length - 1].approvedAt,
          potentialTimeSavings: cleanTimesheets.length * this.AVG_REVIEW_TIME_MINUTES,
        });
      }
    });

    return patterns;
  }

  private static detectHourRangePatterns(timesheets: Timesheet[]): ApprovalPattern[] {
    const patterns: ApprovalPattern[] = [];

    // Pattern: Low hours (< 20 hours/week)
    const lowHourTimesheets = timesheets.filter(ts => {
      const totalHours = this.calculateTotalHours(ts);
      return totalHours > 0 && totalHours <= 20;
    });

    if (lowHourTimesheets.length >= this.MIN_PATTERN_MATCHES) {
      const confidenceScore = (lowHourTimesheets.length / timesheets.length) * 100;
      
      if (confidenceScore >= this.MEDIUM_CONFIDENCE_THRESHOLD) {
        patterns.push({
          id: 'hours-low',
          name: 'Part-Time Hours',
          description: 'Automatically approve timesheets with 20 hours or less',
          confidence: this.getConfidenceLevel(confidenceScore),
          confidenceScore,
          matchCount: lowHourTimesheets.length,
          criteria: {
            maxHoursPerWeek: 20,
            excludeAnomalies: true,
          },
          status: 'suggested',
          createdAt: new Date(),
          lastMatchedAt: lowHourTimesheets[lowHourTimesheets.length - 1].approvedAt,
          potentialTimeSavings: lowHourTimesheets.length * this.AVG_REVIEW_TIME_MINUTES,
        });
      }
    }

    return patterns;
  }

  private static detectBillablePatterns(timesheets: Timesheet[]): ApprovalPattern[] {
    const patterns: ApprovalPattern[] = [];

    // This would require project billability info - placeholder for now
    // In real implementation, check if all entries are billable projects

    return patterns;
  }

  /**
   * Check if a timesheet matches any enabled patterns
   */
  static matchTimesheet(
    timesheet: Timesheet,
    employee: Employee,
    enabledPatterns: ApprovalPattern[]
  ): PatternMatch | null {
    for (const pattern of enabledPatterns) {
      const match = this.checkPatternMatch(timesheet, employee, pattern);
      if (match) {
        return match;
      }
    }
    return null;
  }

  private static checkPatternMatch(
    timesheet: Timesheet,
    employee: Employee,
    pattern: ApprovalPattern
  ): PatternMatch | null {
    const criteria = pattern.criteria;
    const matchedCriteria: string[] = [];
    let totalCriteria = 0;

    // Check department
    if (criteria.departments && criteria.departments.length > 0) {
      totalCriteria++;
      if (criteria.departments.includes(employee.department)) {
        matchedCriteria.push(`Department: ${employee.department}`);
      }
    }

    // Check employee
    if (criteria.employees && criteria.employees.length > 0) {
      totalCriteria++;
      if (criteria.employees.includes(timesheet.employeeId)) {
        matchedCriteria.push(`Employee: ${employee.name}`);
      }
    }

    // Check hours
    const totalHours = this.calculateTotalHours(timesheet);
    
    if (criteria.minHoursPerWeek !== undefined) {
      totalCriteria++;
      if (totalHours >= criteria.minHoursPerWeek) {
        matchedCriteria.push(`Hours ≥ ${criteria.minHoursPerWeek}`);
      }
    }

    if (criteria.maxHoursPerWeek !== undefined) {
      totalCriteria++;
      if (totalHours <= criteria.maxHoursPerWeek) {
        matchedCriteria.push(`Hours ≤ ${criteria.maxHoursPerWeek}`);
      }
    }

    // Check weekend work
    if (criteria.excludeWeekendWork) {
      totalCriteria++;
      const hasWeekendWork = timesheet.entries.some(entry => 
        entry.hours.saturday > 0 || entry.hours.sunday > 0
      );
      if (!hasWeekendWork) {
        matchedCriteria.push('No weekend work');
      }
    }

    // Calculate match confidence
    const matchPercentage = totalCriteria > 0 
      ? (matchedCriteria.length / totalCriteria) * 100 
      : 0;

    // Require all criteria to match
    if (matchedCriteria.length === totalCriteria && matchPercentage >= 100) {
      return {
        patternId: pattern.id,
        timesheetId: timesheet.id,
        matchedCriteria,
        confidence: pattern.confidenceScore,
        suggestedAction: pattern.confidenceScore >= this.HIGH_CONFIDENCE_THRESHOLD 
          ? 'auto-approve' 
          : 'review',
      };
    }

    return null;
  }

  /**
   * Calculate insights for automation performance
   */
  static calculateInsights(
    timesheets: Timesheet[],
    patterns: ApprovalPattern[],
    period: 'week' | 'month'
  ): AutomationInsight {
    const enabledPatterns = patterns.filter(p => p.status === 'enabled');
    const daysBack = period === 'week' ? 7 : 30;
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysBack);

    const recentApprovals = timesheets.filter(ts => 
      ts.approvedAt && ts.approvedAt >= cutoffDate
    );

    let patternMatches = 0;
    const patternStats = new Map<string, { count: number; timeSaved: number }>();

    enabledPatterns.forEach(pattern => {
      patternStats.set(pattern.id, { count: 0, timeSaved: 0 });
    });

    // This is simplified - in real implementation, track which timesheets were auto-approved
    recentApprovals.forEach(ts => {
      // Check if it would have matched any pattern
      enabledPatterns.forEach(pattern => {
        // Simplified check - in real implementation, use actual match data
        const stats = patternStats.get(pattern.id)!;
        stats.count++;
        stats.timeSaved += this.AVG_REVIEW_TIME_MINUTES;
        patternMatches++;
      });
    });

    const patternInsights = Array.from(patternStats.entries()).map(([patternId, stats]) => {
      const pattern = patterns.find(p => p.id === patternId)!;
      return {
        patternId,
        name: pattern.name,
        matchCount: stats.count,
        timeSaved: stats.timeSaved,
      };
    });

    return {
      period,
      totalApprovals: recentApprovals.length,
      patternMatches,
      timeSaved: Array.from(patternStats.values()).reduce((sum, s) => sum + s.timeSaved, 0),
      patterns: patternInsights,
    };
  }

  private static calculateTotalHours(timesheet: Timesheet): number {
    return timesheet.entries.reduce((sum, entry) => {
      return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
    }, 0);
  }

  private static getConfidenceLevel(score: number): 'high' | 'medium' | 'low' {
    if (score >= this.HIGH_CONFIDENCE_THRESHOLD) return 'high';
    if (score >= this.MEDIUM_CONFIDENCE_THRESHOLD) return 'medium';
    return 'low';
  }
}
